/**
 * 保险库初始化向导：设置主密码（+可选恢复码）→ 展示一次性恢复码。
 * 恢复码仅返回一次，必须提示用户抄写；主密码忘记后无法找回（零知识）。
 */
import { useState } from 'react';
import { useVault } from '../stores/vault';
import { Button, Card, ErrorBox, Field, Input, Toggle, errMsg } from './ui';
import { Icon } from './icons';

type Step = 'password' | 'recovery' | 'done';

export function InitWizard({ onDone }: { onDone: () => void }) {
  const init = useVault((s) => s.init);
  const [step, setStep] = useState<Step>('password');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [withRecovery, setWithRecovery] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function submit() {
    if (password.length < 10 || !/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      setError('主密码至少 10 位，且包含字母和数字');
      return;
    }
    if (password !== confirm) {
      setError('两次输入的密码不一致');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const result = await init(password, { withRecovery });
      setRecoveryCode(result.recoveryCode ?? null);
      setStep('done');
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mx-auto max-w-md p-6">
      <div className="mb-5 text-center">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-indigo-600 text-white">
          <Icon name="lock" className="h-6 w-6" />
        </div>
        <h2 className="text-lg font-semibold">初始化加密保险库</h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          零知识加密：主密码只在本机派生密钥，服务器无法看到明文
        </p>
      </div>

      <ErrorBox error={error} className="mb-4" />

      {step === 'password' && (
        <div className="space-y-4">
          <Field label="主密码（至少 10 位，含字母和数字）" required hint="忘记主密码将无法解密数据">
            <Input
              type="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="设置一个强主密码"
            />
          </Field>
          <Field label="确认主密码" required>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
            />
          </Field>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-slate-700 dark:text-slate-200">生成恢复码</span>
            <Toggle checked={withRecovery} onChange={setWithRecovery} label="推荐开启" />
          </div>
          <Button className="w-full" onClick={submit} loading={busy}>
            创建保险库
          </Button>
        </div>
      )}

      {step === 'done' && (
        <div className="space-y-4">
          {recoveryCode ? (
            <>
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200">
                请立即抄写以下恢复码并妥善保存。它只显示这一次，丢失后无法再找回。
              </div>
              <div className="rounded-lg bg-slate-100 p-4 text-center font-mono text-lg tracking-widest dark:bg-slate-800">
                {recoveryCode}
              </div>
              <div className="flex items-center gap-2">
                <Toggle checked={saved} onChange={setSaved} label="我已抄写并保存恢复码" />
              </div>
              <Button className="w-full" disabled={!saved} onClick={onDone}>
                进入保险库
              </Button>
            </>
          ) : (
            <>
              <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200">
                保险库已创建。未启用恢复码，请牢记主密码。
              </div>
              <Button className="w-full" onClick={onDone}>
                进入保险库
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}
